/**
 * Sidebar — async unread email count badge.
 *
 * Phase 21.D migration: replaces the IIFE that used to live at the
 * bottom of ``templates/office/components/_sidebar.html`` and polled
 * ``office:api_email_unread_count`` every 30 s to refresh the
 * ``#email-unread-badge`` chip in the "Inbox" sidebar entry.
 *
 * Architecture
 * ------------
 *
 * - **Single setInterval per SPA lifetime.** The sidebar is OOB-
 *   swapped on every boosted navigation (``hx-select-oob="#sidebar"``
 *   in ``_sidebar.html``), and the original inline IIFE re-installed
 *   a fresh ``setInterval`` on every swap, leaking timers across
 *   navigations (after 10 nav events the user had 10 polling timers
 *   firing simultaneously). We bind ONCE per SPA lifetime via the
 *   module-level ``_intervalId`` guard. The badge element id stays
 *   stable across OOB swaps so a single recurring lookup keeps
 *   working.
 *
 * - **URL discovery from the badge dataset, with a window fallback.**
 *   Reverse-resolved Django URLs cannot be hard-coded in an ESM
 *   module (no ``{% url %}`` available). The template now writes the
 *   URL on the badge as ``data-url`` so the JS module is a single
 *   source of truth for the polling logic, while the URL itself
 *   stays server-driven.
 *
 * - **Idempotent display toggle.** The badge starts in either visible
 *   or ``display:none`` state depending on whether the context
 *   processor seeded a non-zero count. The first poll then aligns
 *   it with the cache value. Subsequent polls flip ``style.display``
 *   between ``inline-flex`` and ``none``.
 *
 * - **Error tolerance.** The endpoint is best-effort (no point in
 *   blowing the user's console with a stack trace if the network
 *   blips); we log a single ``console.warn`` and let the next tick
 *   recover. The original inline implementation did the same.
 *
 * Auto-init contract: the ``<aside id="sidebar">`` carries
 * ``data-auto-init="sidebarEmailBadge"``. ``app.js`` calls
 * ``App.pages.autoInitSidebarEmailBadge()`` on initial load. Because
 * the OOB swap doesn't re-run ``_runAutoInit`` (only ``#main-content``
 * etc. trigger it), and the body-level interval survives swaps, no
 * extra wiring is needed for boosted navs.
 */

const POLL_INTERVAL_MS = 30000;
const BADGE_ID = 'email-unread-badge';

let _intervalId = null;
let _pollUrl = '';

function findBadge() {
  return document.getElementById(BADGE_ID);
}

function applyCount(count) {
  const badge = findBadge();
  if (!badge) return;
  if (Number(count) > 0) {
    badge.textContent = String(count);
    badge.style.display = 'inline-flex';
  } else {
    badge.style.display = 'none';
  }
}

function refreshBadge() {
  const badge = findBadge();
  if (!badge || !_pollUrl) return;
  fetch(_pollUrl, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })
    .then((r) => {
      if (!r.ok) {
        console.warn('[sidebar-email-badge] unread-count response:', r.status);
        return null;
      }
      return r.json();
    })
    .then((data) => {
      if (!data) return;
      applyCount(data.count);
    })
    .catch((err) => {
      console.warn('[sidebar-email-badge] fetch error:', err);
    });
}

export function initSidebarEmailBadge() {
  const badge = findBadge();
  if (!badge) return;
  const url = badge.getAttribute('data-url');
  if (!url) return;
  _pollUrl = url;
  refreshBadge();
  if (_intervalId !== null) return;
  _intervalId = window.setInterval(refreshBadge, POLL_INTERVAL_MS);
}

export function autoInitSidebarEmailBadge() {
  initSidebarEmailBadge();
}
